/**
 * 화면 오류 안전망 (Error Boundary) — 하얀화면 멈춤 방지.
 *
 * 두 가지 멈춤을 모두 처리:
 *   1) 렌더 중 오류 → 화면 전체가 죽는(하얀화면) 대신 "새로고침" 안내 카드 표시.
 *   2) 배포로 파일(청크)이 바뀐 경우 → 옛 탭이 사라진 파일을 못 받아 실패 →
 *      자동으로 한 번 새로고침해 최신 빌드를 받아 스스로 복구 (무한루프 가드).
 *
 * resetKey(예: 라우트 경로)가 바뀌면 안전망을 자동 리셋 → 다른 메뉴로 이동 시 복구.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

const RELOAD_FLAG = 'sm-chunk-reload-at';
// 직전 자동 새로고침 후 이 시간 안에 또 청크 오류면 = 새로고침으로도 안 풀림 →
// 무한 새로고침 루프 방지를 위해 더는 자동 새로고침하지 않고 안내 카드 표시.
const RELOAD_COOLDOWN_MS = 20_000;

/** 청크/모듈 로드 실패(배포로 옛 파일이 사라짐)인지 판별 */
export function isChunkLoadError(err: unknown): boolean {
  const msg = err instanceof Error ? `${err.name}: ${err.message}` : String(err ?? '');
  return /dynamically imported module|Failed to fetch|Importing a module script failed|ChunkLoadError|error loading dynamically imported|module script failed|Loading chunk \d+ failed/i.test(
    msg,
  );
}

/**
 * 청크 로드 실패 시 자동 새로고침 1회 (무한 루프 가드).
 * @returns 실제로 새로고침을 실행했으면 true, 가드에 막혔으면 false.
 */
export function reloadOnceForChunkError(): boolean {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(RELOAD_FLAG) || '0');
  } catch {
    /* sessionStorage 비활성 환경 무시 */
  }
  const now = Date.now();
  if (now - last < RELOAD_COOLDOWN_MS) return false; // 직전에 이미 새로고침함 → 루프 방지
  try {
    sessionStorage.setItem(RELOAD_FLAG, String(now));
  } catch {
    /* noop */
  }
  window.location.reload();
  return true;
}

type Props = {
  children: ReactNode;
  /** 값이 바뀌면 안전망 자동 리셋 (예: 라우트 경로) — 다른 메뉴로 이동 시 자동 복구 */
  resetKey?: string;
};
type State = { error: Error | null; reloading: boolean };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, reloading: false };

  static getDerivedStateFromError(error: Error): State {
    // 청크 오류면 곧 자동 새로고침 → 오류 카드 깜빡임 없이 "업데이트 중" 화면부터.
    return { error, reloading: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    if (isChunkLoadError(error)) {
      const reloaded = reloadOnceForChunkError();
      // 가드에 막혀 새로고침을 못 했으면 안내 카드로 전환.
      if (!reloaded) this.setState({ reloading: false });
    }
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null, reloading: false });
    }
  }

  render() {
    const { error, reloading } = this.state;
    if (!error) return this.props.children;

    if (reloading) {
      return (
        <div className="flex h-full min-h-[50vh] items-center justify-center p-6">
          <div className="text-center text-slate-500">
            <div className="text-3xl mb-2">🔄</div>
            <div className="text-sm">최신 버전으로 업데이트 중…</div>
          </div>
        </div>
      );
    }

    const chunk = isChunkLoadError(error);
    return (
      <div className="flex h-full min-h-[50vh] items-center justify-center p-6">
        <div className="max-w-md text-center">
          <div className="mb-3 text-5xl">⚠️</div>
          <h2 className="ui-modal-title">
            {chunk ? '새 버전이 배포되었어요' : '일시적인 문제가 발생했어요'}
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">
            {chunk
              ? '새로고침하면 최신 화면으로 이어집니다.'
              : '새로고침하면 대부분 해결됩니다. 계속 같은 문제가 나오면 관리자에게 알려주세요.'}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="btn btn-primary mt-4"
          >
            새로고침
          </button>
        </div>
      </div>
    );
  }
}
